// This is the same logic used in the metadata utils in the inspection service to flatten JSON.
// So these keys (period-separated joins of the nested field names) will be matched and correctly processed

import { readString } from 'react-papaparse';

import {
  type AtlasGqlGetAssetForInspectionQuery,
  AtlasGqlFileFieldDelimiter,
} from 'types/atlas-graphql';
import { ASSET_TYPE_BLADE, ASSET_TYPE_TURBINE } from 'utils/constants';
import { isCorrectColumnCount } from 'utils/csv/helpers';
import { type NonNullPath } from 'utils/types';

import {
  type BladeExternalAssetDescriptors,
  type BladeInternalAssetDescriptors,
  parseInspectionUploadPath,
} from '../pathParsers';
import { type InspectionTypeName } from '../types';

// Use that same logic here to ensure consistency
export const flatParseJSON = (jsonString: string) => {
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      return flattenObjectsInArray(parsed);
    } else {
      return flattenObject(parsed);
    }
  } catch (e) {
    throw new Error('Invalid JSON!');
  }
};

export const getProvidedFieldNamesFromJsonRecords = (
  records: Record<string, unknown>[]
): string[] => {
  const seen = new Set<string>();
  const providedFieldNames: string[] = [];

  records.forEach(record => {
    Object.keys(record).forEach(key => {
      if (key === 'Notes' || seen.has(key)) {
        return;
      }

      seen.add(key);
      providedFieldNames.push(key);
    });
  });

  return providedFieldNames;
};

export const parseCsv = (
  csvText: string,
  cellDelimiter?: AtlasGqlFileFieldDelimiter
): {
  meta: {
    [key: string]: any;
    delimiter: string;
    fields: string[];
  };
  data: {
    [key: string]: string;
  }[];
  delimiter: AtlasGqlFileFieldDelimiter;
  likelyDelimiterError: boolean;
  error?: string;
} => {
  const {
    data,
    meta,
  }: {
    data: { [key: string]: string }[];
    meta: { delimiter: string; fields: string[]; [key: string]: any };
  } = readString(csvText, {
    header: true,
    skipEmptyLines: true,
    /**
     * Papaparse will auto-detect the delimiter by default and return it in
     * meta.delimiter. On first upload, this detected delimiter is set in state
     * (which then gets passed in the mutation). After cellDelimiter is set (either
     * the detected one or what is set by the user), it is passed explicitly
     * and the file is re-parsed.
     */
    ...(cellDelimiter
      ? {
          delimiter: {
            COMMA: ',',
            SEMICOLON: ';',
            TAB: '\t',
          }[cellDelimiter],
        }
      : {}),
  });

  let delimiter = AtlasGqlFileFieldDelimiter.Comma;
  if (meta.delimiter === ';') {
    delimiter = AtlasGqlFileFieldDelimiter.Semicolon;
  } else if (meta.delimiter === '\t') {
    delimiter = AtlasGqlFileFieldDelimiter.Tab;
  }

  const likelyDelimiterError = meta.fields.length === 1;

  let error;
  if (!likelyDelimiterError && !isCorrectColumnCount(meta.fields, data)) {
    error =
      'Mismatch in columns between header and data rows. Please check your file and reupload.';
  } else if (!data.length) {
    error = 'File contained no data. Please check your file and reupload.';
  }

  return {
    meta,
    data,
    delimiter,
    likelyDelimiterError,
    error,
  };
};

const flattenObject = (
  object: Record<string, any>,
  prefix: string = '',
  separator: string = '.'
): Record<string, any> => {
  // Make an empty object to hold everything
  const result: Record<string, any> = {};
  // We're going to iterate through a stack, adding items to it as we drill deeper
  const stack: Array<{ obj: Record<string, any>; keyPrefix: string }> = [
    { obj: object, keyPrefix: prefix },
  ];

  while (stack.length > 0) {
    // Pull the last item off the stack, since we've added everything to it, we know it can't be null
    const { obj, keyPrefix } = stack.pop()!;

    Object.entries(obj).forEach(([key, value]) => {
      // Build a new key that aggregates the provided prefix (if applicable)
      const newKey = keyPrefix ? `${keyPrefix}${separator}${key}` : key;

      // if we're working with an object, push that on the stack to be broken down
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        stack.push({ obj: value, keyPrefix: newKey });
      } else {
        // If we have the value and it's not an object nor an array, add that to the result object with the aggregated key as the key
        result[newKey] = value;
      }
    });
  }

  return result;
};

const flattenObjectsInArray = (object: Record<string, any>[]): Record<string, any>[] => {
  return object.map(val => flattenObject(val));
};

type AssetForInspection = NonNullPath<AtlasGqlGetAssetForInspectionQuery, ['inspection', 'asset']>;

export const getFolderMethodAssetDescriptors = (
  path: string,
  inspectionTypeName: InspectionTypeName | undefined,
  inspectionAsset: AssetForInspection | undefined
) => {
  const assetDescriptors = parseInspectionUploadPath(path, inspectionTypeName);
  const bladeDescriptors = assetDescriptors as
    | BladeExternalAssetDescriptors
    | BladeInternalAssetDescriptors;

  if (inspectionAsset?.assetType?.name === ASSET_TYPE_BLADE) {
    // overwrite the blade position even if the folder structure provides a different position
    if ('position' in inspectionAsset && inspectionAsset.position) {
      bladeDescriptors.bladePosition = inspectionAsset.position;
    }
    // a Blade in storage can have no Position, so every image is tied to it by id
    bladeDescriptors.bladeGlobalId = inspectionAsset.id;
  } else if (
    inspectionAsset?.assetType?.name === ASSET_TYPE_TURBINE &&
    bladeDescriptors.bladePosition
  ) {
    const blade = inspectionAsset.children?.find(
      child =>
        child?.assetType?.name === ASSET_TYPE_BLADE &&
        'position' in child &&
        child.position === bladeDescriptors.bladePosition
    );
    if (blade) {
      bladeDescriptors.bladeGlobalId = blade.id;
    }
  }

  return assetDescriptors;
};
